import graphql__5f6ad5a88950284b842f from "./__generated__/AddEmailTemplateVariantDialogMutation.graphql.ts";import * as React from "react";
import {graphql} from "react-relay";
import {useRelayEnvironment} from "react-relay/hooks";
import FormDialog from "./FormDialog";
import Section from "../form/Section";
import Row from "../form/Row";
import Label from "../form/Label";
import Field from "../form/Field";
import * as yup from "yup";
import {commitMutationWithErrorHandling} from "../../helper/commitMutationWithErrorHandling";
import {useErrorContext} from "../../contexts/ErrorContext";
import {useIntl} from "react-intl";

type Props = {
    emailTemplateId: string;
    existingLanguages: string[];
    handleCloseDialog: () => void;
}

const AVAILABLE_LANGUAGES = [
    {code: 'de', label: 'Deutsch'},
    {code: 'en', label: 'English'},
];

const AddEmailTemplateVariantDialog: React.FC<Props> = ({
    emailTemplateId,
    existingLanguages,
    handleCloseDialog
}) => {
    const intl = useIntl();
    const relayEnvironment = useRelayEnvironment();
    const {addError} = useErrorContext();

    // Filter out languages that already have variants
    const availableLanguages = AVAILABLE_LANGUAGES.filter(
        lang => !existingLanguages.includes(lang.code)
    );

    const handleSubmission = (values) => {
        commitMutationWithErrorHandling(
            relayEnvironment,
            {
                mutation: graphql__5f6ad5a88950284b842f,
                variables: {
                    input: {
                        id: emailTemplateId,
                        language: values.language,
                        subject: values.subject,
                        preheader: values.preheader || null,
                        htmlContent: values.htmlContent || '',
                        textContent: values.textContent || ''
                    }
                },
                onCompleted: () => {
                    handleCloseDialog();
                },
                onError: (error) => {
                    console.error(error);
                    handleCloseDialog();
                }
            },
            addError,
            {
                customErrorMessage: intl.formatMessage({id: 'errors.failedToAddEmailTemplateVariant'})
            }
        )
    }

    if (availableLanguages.length === 0) {
        return (
            <FormDialog
                handleCloseDialog={handleCloseDialog}
                handleSubmission={() => handleCloseDialog()}
                initialValues={{}}
                headline={intl.formatMessage({id: 'dialog.AddEmailTemplateVariantDialog.headline'})}
                submitLabel={intl.formatMessage({id: 'button.close'})}
            >
                <p className="text-gray-600">
                    {intl.formatMessage({id: 'dialog.AddEmailTemplateVariantDialog.allLanguagesUsed'})}
                </p>
            </FormDialog>
        );
    }

    const initialValues = {
        language: availableLanguages[0]?.code || '',
        subject: '',
        preheader: '',
        htmlContent: '',
        textContent: ''
    }

    const validationSchema = yup.object().shape({
        language: yup.string()
            .required(intl.formatMessage({id: 'validation.field.required'})),
        subject: yup.string()
            .required(intl.formatMessage({id: 'validation.field.required'})),
    });

    return (
        <FormDialog
            handleCloseDialog={handleCloseDialog}
            handleSubmission={handleSubmission}
            initialValues={initialValues}
            validationSchema={validationSchema}
            headline={intl.formatMessage({id: 'dialog.AddEmailTemplateVariantDialog.headline'})}
            submitLabel={intl.formatMessage({id: 'button.addLanguage'})}
        >
            <p className="text-gray-600 mb-4">
                {intl.formatMessage({id: 'dialog.AddEmailTemplateVariantDialog.description'})}
            </p>
            <Section>
                <Row>
                    <Label>{intl.formatMessage({id: 'emailTemplate.field.language'})}</Label>
                    <Field name="language" as="select">
                        {availableLanguages.map(lang => (
                            <option key={lang.code} value={lang.code}>{lang.label}</option>
                        ))}
                    </Field>
                </Row>
                <Row>
                    <Label>{intl.formatMessage({id: 'emailTemplate.field.subject'})}</Label>
                    <Field
                        name="subject"
                        type="text"
                        placeholder={intl.formatMessage({id: 'emailTemplate.placeholder.subject'})}
                    />
                </Row>
            </Section>
        </FormDialog>
    )
}

export default AddEmailTemplateVariantDialog;
